import { parseTwitchUrl, twitchWatchUrl } from "./twitch";
// Originally taken from here - https://stackoverflow.com/a/6041965
const URL_REGEX = new RegExp(`(https?):\\/\\/(localhost|[\\w_-]+(?:(?:\\.[\\w_-]+)+))([\\w.,@?^=%&:\\/~+#-]*[\\w@?^=%&\\/~+#-])`, "g");
const communityMessageRegex = /\/community\/([a-z0-9_-]+)\/channel\/(\d+)\/(\d+)(?:\/(\d+))?/i;
const groupMessageRegex = /\/group\/([a-z0-9_-]+)\/(\d+)(?:\/(\d+))?/i;
const LINK_REMOVED = "#LINK_REMOVED";
const LINK_REMOVED_REGEX = new RegExp(LINK_REMOVED, "g");
export const MAX_LINK_PREVIEWS = 3;
// Bounded caches: Map preserves insertion order, so deleting the first key evicts the oldest entry
export const OG_CACHE_MAX = 300;
const ogPromiseCache = new Map();
const ogResolvedCache = new Map();
function cacheSet(cache, key, value) {
    cache.delete(key);
    cache.set(key, value);
    if (cache.size > OG_CACHE_MAX) {
        cache.delete(cache.keys().next().value);
    }
}
export function ogCacheSizes() {
    return { promises: ogPromiseCache.size, resolved: ogResolvedCache.size };
}
export function getCachedOgData(url) {
    return ogResolvedCache.get(url); // undefined = not yet resolved
}
export function stripLinkDisabledMarker(text) {
    return text.replace(LINK_REMOVED_REGEX, () => "");
}
export function removeOpenGraphPreviews(msg, urls) {
    return {
        ...msg,
        ogPreviews: msg.ogPreviews.filter((p) => !urls.find((u) => p.url === u)),
    };
}
export function ogPreviewCard(preview) {
    let url;
    try {
        url = new URL(preview.url);
    }
    catch {
        url = undefined;
    }
    const image = preview.image !== undefined && preview.image.width > 0 && preview.image.height > 0
        ? preview.image
        : undefined;
    return {
        href: url?.protocol === "http:" || url?.protocol === "https:" ? preview.url : undefined,
        hostname: url?.hostname.replace(/^www\./, "") ?? preview.url,
        image,
        sideBySide: image !== undefined && image.width <= image.height * 1.2,
    };
}
function extractRawUrls(text) {
    const withoutMarkdownDisplayText = text.replace(/\[[^\]]*\]\((https?:\/\/[^)]*)\)/g, "$1");
    return [...new Set(withoutMarkdownDisplayText.match(URL_REGEX) ?? [])];
}
function isOcUrl(url) {
    if (typeof url === "string") {
        try {
            url = new URL(url);
        }
        catch {
            return false;
        }
    }
    return (url.hostname === "oc.app" ||
        url.hostname.endsWith(".oc.app") ||
        url.hostname === "localhost");
}
function parseMessageUrl(urlText) {
    let url;
    try {
        url = new URL(urlText);
    }
    catch {
        return;
    }
    if (!isOcUrl(url))
        return;
    let m = url.pathname.match(communityMessageRegex);
    if (m) {
        return {
            kind: "message",
            url: urlText,
            chatId: {
                kind: "channel",
                communityId: m[1],
                channelId: Number(m[2]),
            },
            threadRootMessageIndex: m[4] ? Number(m[3]) : undefined,
            messageIndex: m[4] ? Number(m[4]) : Number(m[3]),
        };
    }
    m = url.pathname.match(groupMessageRegex);
    if (m) {
        return {
            kind: "message",
            url: urlText,
            chatId: {
                kind: "group_chat",
                groupId: m[1],
            },
            threadRootMessageIndex: m[3] ? Number(m[2]) : undefined,
            messageIndex: m[3] ? Number(m[3]) : Number(m[2]),
        };
    }
}
export function classifyUrl(url) {
    return parseMessageUrl(url);
}
// Used by the agent layer to extract OC message previews from message text.
// No limit applied; LINK_REMOVED-suffixed URLs are skipped entirely.
export function extractMessagePreviews(text) {
    return extractRawUrls(text)
        .filter((u) => !u.endsWith(LINK_REMOVED))
        .reduce((previews, url) => {
        const preview = parseMessageUrl(url);
        if (preview)
            previews.push(preview);
        return previews;
    }, []);
}
// The links a message shows previews for, as written: a link disabled by the legacy marker still
// ends in it
function previewedLinks(text) {
    return extractRawUrls(text)
        .filter((url) => classifyUrl(withoutLinkRemoved(url)) === undefined)
        .slice(0, MAX_LINK_PREVIEWS);
}
function withoutLinkRemoved(url) {
    return url.endsWith(LINK_REMOVED) ? url.substring(0, url.length - LINK_REMOVED.length) : url;
}
// The Twitch links among those a message shows previews for, which play in it, one per channel,
// clip, or video and start time. The sender still fetches their OG previews, for the posters.
export function extractTwitchEmbeds(text) {
    const embeds = new Map();
    for (const url of previewedLinks(text)) {
        if (url.endsWith(LINK_REMOVED))
            continue;
        const embed = parseTwitchUrl(url);
        if (embed === undefined)
            continue;
        const key = twitchWatchUrl(embed);
        if (!embeds.has(key))
            embeds.set(key, embed);
    }
    return [...embeds.values()];
}
// Used by the client layer to get URLs to fetch OG previews for.
// Strips LINK_REMOVED suffix, limits to MAX_LINK_PREVIEWS, and excludes OC message URLs.
export function extractEnabledLinks(text) {
    if (!text)
        return [];
    return previewedLinks(text).map(withoutLinkRemoved);
}
function getImageDimensions(url) {
    return new Promise((resolve) => {
        const img = new Image();
        const timer = setTimeout(() => {
            img.onload = null;
            img.onerror = null;
            img.src = "";
            resolve(undefined);
        }, 5000);
        img.onload = () => {
            clearTimeout(timer);
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = () => {
            clearTimeout(timer);
            resolve(undefined);
        };
        img.src = url;
    });
}
export function fetchOgData(url, proxyUrl) {
    if (!proxyUrl)
        return Promise.resolve(null);
    const cached = ogPromiseCache.get(url);
    if (cached !== undefined)
        return cached;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    const promise = fetch(`${proxyUrl}/preview?url=${encodeURIComponent(url)}`, {
        signal: controller.signal,
    })
        .finally(() => clearTimeout(timeoutId))
        .then((r) => {
        if (!r.ok)
            return null;
        return r.json();
    })
        .then((data) => (data?.title ? data : null))
        .catch(() => null)
        .then((data) => {
        if (data !== null)
            cacheSet(ogResolvedCache, url, data);
        else
            ogPromiseCache.delete(url); // don't cache failures — let next mount retry
        return data;
    });
    cacheSet(ogPromiseCache, url, promise);
    return promise;
}
async function fetchSinglePreview(url, proxyUrl) {
    const data = await fetchOgData(url, proxyUrl);
    if (!data || !data.title)
        return null;
    const preview = {
        kind: "opengraph",
        url,
        title: data.title,
        description: data.description ?? "",
    };
    if (data.image) {
        let width = data.imageWidth;
        let height = data.imageHeight;
        if (width === undefined || height === undefined) {
            // The preview service only reports dimensions when the source page
            // provides og:image:width/height, so fall back to measuring in the
            // browser - otherwise GenericPreview drops the image altogether.
            const dims = await getImageDimensions(data.image);
            width = dims?.width;
            height = dims?.height;
        }
        if (width !== undefined && height !== undefined) {
            preview.image = { url: data.image, width, height };
        }
    }
    return preview;
}
export async function fetchOgPreviews(urls, proxyUrl) {
    const results = await Promise.all(urls.map((url) => fetchSinglePreview(url, proxyUrl).catch(() => null)));
    return results.filter((r) => r !== null);
}
